import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  Building2,
  GitBranch,
  Lock,
  Menu as MenuIcon,
  ShieldCheck,
  User,
  type LucideIcon,
} from 'lucide-react';
import { SOVEREIGN } from '../../../epc/utils/epcProjectVisual';
import { useRoleEditorContext } from './RoleEditorContext';
import { useSectionCompletion } from './useSectionCompletion';
import type {
  RoleEditorFormValues,
  SectionCompletion,
  SectionMeta,
} from './types';

/**
 * Canonical section metadata for the Edit Role view. Exported so other
 * surfaces (header, shell, scroll-spy) can stay in lock-step with the
 * sidebar without duplicating the literal order or copy.
 */
export const ROLE_EDITOR_SECTIONS: SectionMeta[] = [
  {
    key: 'identity',
    label: 'Identity',
    sub: 'Name, description and account type',
    stepIndex: 1,
    iconKey: 'user',
  },
  {
    key: 'department',
    label: 'Department',
    sub: 'Which BU this role belongs to',
    stepIndex: 2,
    iconKey: 'building',
  },
  {
    key: 'access',
    label: 'Access type',
    sub: 'Approver, requester, or both',
    stepIndex: 3,
    iconKey: 'shield',
  },
  {
    key: 'approval',
    label: 'Approval chain',
    sub: 'Who approves submissions from this role',
    stepIndex: 4,
    iconKey: 'workflow',
  },
  {
    key: 'permissions',
    label: 'Permissions',
    sub: 'Module-action grants and EPC tiers',
    stepIndex: 5,
    iconKey: 'lock',
  },
  {
    key: 'menu',
    label: 'Menu order',
    sub: 'Sidebar customisation for users in this role',
    stepIndex: 6,
    iconKey: 'menu',
  },
];

const ICONS: Record<SectionMeta['iconKey'], LucideIcon> = {
  user: User,
  building: Building2,
  shield: ShieldCheck,
  workflow: GitBranch,
  lock: Lock,
  menu: MenuIcon,
};

const EASE_OUT_EXPO: [number, number, number, number] = [0.22, 1, 0.36, 1];

interface DotProps {
  state: SectionCompletion;
}

/**
 * The status dot for a sidebar row. The `current` state animates its halo
 * in on mount and pulses gently forever (the only infinite motion in the
 * sidebar). Respects `prefers-reduced-motion`.
 */
function StatusDot({ state }: DotProps) {
  if (state === 'optional') {
    return (
      <span
        className="inline-block h-2 w-2 rounded-full shrink-0"
        style={{ border: `1px solid ${SOVEREIGN.cardBorder}`, background: 'transparent' }}
        aria-hidden
      />
    );
  }

  if (state === 'current') {
    return (
      <motion.span
        initial={{ boxShadow: `0 0 0 0px ${SOVEREIGN.brand}33` }}
        animate={{ boxShadow: `0 0 0 4px ${SOVEREIGN.brand}33` }}
        transition={{ duration: 0.3, ease: EASE_OUT_EXPO }}
        className="inline-block h-2 w-2 rounded-full shrink-0 animate-pulse motion-reduce:animate-none"
        style={{ backgroundColor: SOVEREIGN.brand }}
        aria-hidden
      />
    );
  }

  const bg =
    state === 'done'
      ? SOVEREIGN.brand
      : state === 'warn'
        ? SOVEREIGN.champagne
        : SOVEREIGN.barTrack; // todo

  return (
    <span
      className="inline-block h-2 w-2 rounded-full shrink-0"
      style={{ backgroundColor: bg }}
      aria-hidden
    />
  );
}

export default function RoleEditorSidebar() {
  const {
    control,
  } = useFormContext<RoleEditorFormValues>();
  const {
    isMobileUser,
    activeSection,
    setActiveSection,
    approvalChain,
    editingRole,
  } = useRoleEditorContext();

  const watched = useWatch({
    control,
    name: ['name', 'description', 'permissions', 'roleType', 'approverRoleId', 'department'],
  });
  const [name, description, permissions, roleType, approverRoleId, department] =
    watched ?? [];

  const completion = useSectionCompletion({
    values: {
      name: name ?? '',
      description: description ?? '',
      permissions: (permissions ?? {}) as Record<string, string[]>,
      roleType: roleType as RoleEditorFormValues['roleType'],
      approverRoleId: approverRoleId as string | undefined,
      department: department as string | undefined,
    },
    approvalChain,
    isMobileUser,
    isEditing: Boolean(editingRole),
    activeSection,
  });

  // Visible sections (drop any `na`) and renumber over the visible subset.
  const visibleSections = useMemo(() => {
    return ROLE_EDITOR_SECTIONS.filter((s) => completion[s.key] !== 'na').map(
      (s, idx) => ({ ...s, stepIndex: idx + 1 }),
    );
  }, [completion]);

  const totalCount = visibleSections.length;
  const doneCount = useMemo(
    () =>
      visibleSections.filter(
        (s) => completion[s.key] === 'done' || completion[s.key] === 'current',
      ).length,
    [visibleSections, completion],
  );

  const progressPct = totalCount === 0 ? 0 : (doneCount * 100) / totalCount;

  return (
    <aside
      className="sticky top-6 w-[240px] shrink-0 self-start rounded-2xl p-2"
      style={{
        border: `1px solid ${SOVEREIGN.cardBorder}`,
        background: SOVEREIGN.cardBg,
        boxShadow: SOVEREIGN.cardShadow,
      }}
      aria-label="Edit role sections"
    >
      <p
        className="px-3 pt-2 pb-2 text-[10px] font-medium uppercase tracking-[0.12em]"
        style={{ color: SOVEREIGN.kpiCaption }}
      >
        Configure role
      </p>

      <nav className="flex flex-col gap-0.5">
        {visibleSections.map((section) => {
          const state = completion[section.key];
          const Icon = ICONS[section.iconKey];
          const isActive = activeSection === section.key;

          return (
            <motion.button
              key={section.key}
              type="button"
              onClick={() => setActiveSection(section.key)}
              whileTap={{ scale: 0.97 }}
              transition={{ duration: 0.15, ease: EASE_OUT_EXPO }}
              className="group flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left transition-colors"
              style={
                isActive
                  ? {
                      background: 'rgba(31,90,71,0.08)',
                      border: `1px solid ${SOVEREIGN.cardBorder}`,
                      color: SOVEREIGN.title,
                      fontWeight: 600,
                    }
                  : {
                      color: SOVEREIGN.kpiCaption,
                      border: '1px solid transparent',
                    }
              }
              aria-current={isActive ? 'step' : undefined}
            >
              <StatusDot state={state} />
              <Icon
                size={14}
                strokeWidth={2}
                style={{ color: isActive ? SOVEREIGN.bottle : SOVEREIGN.kpiCaption }}
                aria-hidden
              />
              <span className="text-[13px] leading-tight">{section.label}</span>
              <span
                className="ml-auto font-mono text-[10px]"
                style={{ color: isActive ? SOVEREIGN.bottle : SOVEREIGN.barTrack }}
              >
                {String(section.stepIndex).padStart(2, '0')}
              </span>
            </motion.button>
          );
        })}
      </nav>

      <div className="mt-4 px-3">
        <p
          className="pb-2 text-[10px] font-medium uppercase tracking-[0.12em]"
          style={{ color: SOVEREIGN.kpiCaption }}
        >
          Progress
        </p>
        <div
          className="h-[6px] w-full overflow-hidden rounded-full"
          style={{ background: SOVEREIGN.barTrack }}
        >
          <motion.div
            className="h-full rounded-full"
            initial={false}
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.6, ease: EASE_OUT_EXPO }}
            style={{ background: SOVEREIGN.barFill }}
          />
        </div>
        <p
          className="mt-1.5 text-[10.5px]"
          style={{ color: SOVEREIGN.kpiCaption }}
        >
          {doneCount} of {totalCount} complete
        </p>
      </div>
    </aside>
  );
}
